import { agreementProgress, rollOverAmount } from '@platform/shared-types';

/**
 * The arithmetic behind the tenant Finance tab, kept out of the components so
 * every number on that screen can be tested.
 *
 * Two rules run through all of it:
 *   - Money is INTEGER CENTS everywhere. Formatting happens once, at display.
 *   - Percentage paid is `paidCents / principalCents` and comes from
 *     shared-types' `agreementProgress`. It is NEVER derived from elapsed time:
 *     a missed payment must not look like a paid one.
 */

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Integer cents -> `$1,416.00`. The single money formatter for these panels. */
export const money = (cents?: number | null, currency = 'usd'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((cents ?? 0) / 100);

/**
 * Stripe hands out epoch SECONDS; DynamoDB rows here hold epoch MILLISECONDS.
 * Anything below this threshold (2000-01-01 in ms) can only be seconds, so it
 * is scaled up. Without it a 2026 timestamp in seconds renders as 1970.
 */
const MS_THRESHOLD = 946684800000;

export const toMillis = (ts?: number | null): number | null => {
  if (ts === undefined || ts === null || !Number.isFinite(ts) || ts <= 0) return null;
  return ts < MS_THRESHOLD ? ts * 1000 : ts;
};

export const shortDate = (ts?: number | null): string => {
  const ms = toMillis(ts);
  return ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};

export const shortDateTime = (ts?: number | null): string => {
  const ms = toMillis(ts);
  return ms
    ? new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
};

// ---------------------------------------------------------------------------
// Next invoice
// ---------------------------------------------------------------------------

/** Just enough of a Stripe subscription to answer "when is the next invoice?". */
export interface SubscriptionLike {
  status?: string;
  current_period_end?: number | null;
  cancel_at_period_end?: boolean | null;
  items?: { data?: Array<{ current_period_end?: number | null }> };
}

export interface NextInvoice {
  /** Epoch ms, or null when nothing on the subscription says. */
  atMs: number | null;
  /** What a human reads in the cell — never a guess dressed as a date. */
  label: string;
  /** True when the period end is the END of the subscription, not a renewal. */
  final: boolean;
}

/**
 * When this subscription next bills.
 *
 * `current_period_end` moved onto the subscription ITEM in later Stripe API
 * versions, so the item is read as a fallback rather than reporting "—" on a
 * subscription that plainly has a period. A subscription set to cancel at period
 * end has no NEXT invoice — that date is when it stops, and saying "1 Sep" in a
 * column headed "Next invoice" would state the opposite of the truth.
 */
export function nextInvoice(sub?: SubscriptionLike | null): NextInvoice {
  const raw = sub?.current_period_end ?? sub?.items?.data?.[0]?.current_period_end ?? null;
  const atMs = toMillis(raw);
  if (!sub || atMs === null) return { atMs: null, label: '—', final: false };
  if (sub.cancel_at_period_end) return { atMs, label: `ends ${shortDate(atMs)}`, final: true };
  if (sub.status === 'canceled') return { atMs, label: 'cancelled', final: true };
  return { atMs, label: shortDate(atMs), final: false };
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

/**
 * A location subscription only bills in these states. `pending`,
 * `info_requested` and `rejected` never had a subscription; `inactive` no longer
 * has one. Counting either into MRR overstates what the tenant actually pays.
 *
 * `payment_pending` IS counted: the price is agreed and the subscription exists,
 * it is simply awaiting first payment.
 */
export const BILLING_LOCATION_STATUSES = new Set(['active', 'grace', 'payment_pending', 'deactivation_requested']);

/** The per-location billing request as this screen reads it. */
export interface LocationRow {
  requestId: string;
  tenantId?: string;
  locationId?: string;
  name?: string;
  status?: string;
  isFree?: boolean;
  priceId?: string;
  priceName?: string;
  priceAmount?: number;
  monthlyAmount?: number;
  stripeSubscriptionId?: string;
  paymentStatus?: string;
  [k: string]: any;
}

export const isBillingLocation = (loc: LocationRow): boolean =>
  BILLING_LOCATION_STATUSES.has(loc.status || 'active');

/** What this location adds to the monthly bill. Free and non-billing states are 0. */
export function locationMonthlyCents(loc: LocationRow): number {
  if (!isBillingLocation(loc)) return 0;
  if (loc.isFree) return 0;
  return loc.priceAmount ?? loc.monthlyAmount ?? 0;
}

// ---------------------------------------------------------------------------
// Device agreements
// ---------------------------------------------------------------------------

export interface AgreementLike {
  principalCents?: number;
  paidCents?: number;
  monthlyAmountCents?: number;
  termMonths?: number;
  status?: string;
  skuId?: string;
}

export interface AgreementView {
  /** 0..1, from shared-types. */
  fraction: number;
  /** `12.5%` — one decimal, so a part-paid agreement never reads as 0% or 13%. */
  percentLabel: string;
  /** `$177.00 of $1,416.00 paid` — the mockup's wording. */
  paidLabel: string;
  outstandingCents: number;
}

/**
 * How far through an agreement the customer is.
 *
 * Both numbers come from shared-types (`agreementProgress`, `rollOverAmount`) so
 * the screen and the service can never disagree about what is outstanding.
 */
export function agreementView(a?: AgreementLike | null): AgreementView {
  const principalCents = a?.principalCents ?? 0;
  const paidCents = a?.paidCents ?? 0;
  const fraction = agreementProgress({ principalCents, paidCents });
  return {
    fraction,
    percentLabel: `${(fraction * 100).toFixed(1)}%`,
    paidLabel: `${money(paidCents)} of ${money(principalCents)} paid`,
    outstandingCents: rollOverAmount({ principalCents, paidCents }),
  };
}

/**
 * Agreement statuses the service refuses to act on again — see
 * hardware-service `TERMINAL_AGREEMENT_STATUSES`. `defaulted` is deliberately
 * absent: an agreement that fell behind can still be written off or settled.
 */
export const TERMINAL_AGREEMENT_STATUSES = new Set(['settled', 'waived']);

export type AgreementAction = 'waive' | 'settle' | 'rollover';

export const AGREEMENT_ACTION_LABEL: Record<AgreementAction, string> = {
  waive: 'Waive',
  settle: 'Settle early',
  rollover: 'Roll over',
};

/**
 * Why the service would refuse this action, or null when it would accept it.
 *
 * A mirror of hardware-service's own checks so the UI disables the button rather
 * than letting an operator fill in a reason for an action that comes back 409.
 * The server stays the enforcement point; nothing here is the only guard.
 */
export function agreementActionBlock(a?: AgreementLike | null): string | null {
  if (!a) return 'This device has no finance agreement, so there is nothing to waive or settle.';
  const status = a.status || 'active';
  if (TERMINAL_AGREEMENT_STATUSES.has(status)) {
    return `This agreement is already ${status}. Agreement actions are not reversible — there is no road back through this screen.`;
  }
  return null;
}

/** Why the confirm button stays disabled, or null when the action would be sent. */
export function agreementSubmitBlock(a: AgreementLike | null | undefined, reason: string): string | null {
  const blocked = agreementActionBlock(a);
  if (blocked) return blocked;
  if (!reason.trim()) {
    return 'A reason is required — this moves real money, it is recorded against your name, and it cannot be undone.';
  }
  return null;
}

/**
 * What this action DOES TODAY, in the operator's words.
 *
 * The amount is `principalCents - paidCents` and is computed BY THE SERVER; the
 * same function is used here so the confirmation states the figure the service
 * will use, never a second opinion.
 *
 * The wording is deliberately narrow. §7.3 says a waive cancels the Stripe
 * subscription item and a settlement raises a one-time invoice, but
 * `hardware-service`'s `agreementAction` handler says in as many words that "no
 * Stripe object is created, modified or cancelled here … the cancellation or
 * one-time invoice is a later stage". Until that stage exists, telling an
 * operator their $1,239 settlement has been invoiced would be telling them
 * something untrue about money. What the API really does is record the decision
 * and its audit row — so that is what these strings say.
 */
export function agreementActionEffect(action: AgreementAction, a?: AgreementLike | null): string {
  const outstanding = money(agreementView(a).outstandingCents);
  switch (action) {
    case 'waive':
      return `Records the decision to write off the ${outstanding} still outstanding, with an audit row. Nothing already paid is refunded.`;
    case 'settle':
      return `Records the decision to settle the ${outstanding} still outstanding, with an audit row.`;
    case 'rollover':
      return `Records the decision to close this agreement and carry the ${outstanding} outstanding onto a replacement.`;
  }
}

/**
 * The half that has NOT happened when an agreement action returns 200.
 *
 * Shown next to every waive/settle confirmation and beside a device whose
 * agreement is already terminal, because the Hardware MRR tile drops that
 * device the moment the status flips — which on its own reads as "billing has
 * stopped" when in Stripe it has not.
 */
export const STRIPE_STEP_PENDING =
  'The Stripe side is a separate step and does NOT happen here: no subscription item is cancelled and no '
  + 'invoice is raised. Until that step runs, the tenant keeps being charged — even though this screen and the '
  + 'Hardware total will already show the agreement as closed.';

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export interface FinanceTotalsInput {
  /** The tenant's base plan, in cents per month. */
  basePlanCents?: number;
  locations: LocationRow[];
  /** One entry per financed device unit. */
  agreements: AgreementLike[];
  /** Recurring package/add-on lines, in cents per month. */
  packageCents?: number;
}

export interface FinanceTotals {
  /** Base plan + location subscriptions + packages. */
  softwareCents: number;
  /** Device financing, active agreements only. */
  hardwareCents: number;
  totalCents: number;
  billingLocations: number;
  freeLocations: number;
  financedDevices: number;
  /**
   * Agreements this total has ALREADY stopped counting because they read
   * settled or waived — while `agreementAction` has not touched Stripe, so the
   * charge may still be live. Surfaced so the tile can say so rather than
   * letting a dropped number imply billing stopped.
   */
  closedAgreements: number;
}

/**
 * The four KPI tiles.
 *
 * Hardware counts ACTIVE agreements only: a waived or settled agreement has no
 * recurring item left in Stripe, so including it would invent monthly revenue
 * that stopped being charged the moment the action was taken.
 */
export function financeTotals(input: FinanceTotalsInput): FinanceTotals {
  const billing = input.locations.filter(isBillingLocation);
  const locationCents = billing.reduce((sum, l) => sum + locationMonthlyCents(l), 0);
  const active = input.agreements.filter((a) => (a?.status || 'active') === 'active');
  const closed = input.agreements.filter((a) => TERMINAL_AGREEMENT_STATUSES.has(a?.status || 'active'));
  const hardwareCents = active.reduce((sum, a) => sum + (a.monthlyAmountCents ?? 0), 0);
  const softwareCents = (input.basePlanCents ?? 0) + locationCents + (input.packageCents ?? 0);
  return {
    softwareCents,
    hardwareCents,
    totalCents: softwareCents + hardwareCents,
    billingLocations: billing.length,
    freeLocations: billing.filter((l) => !!l.isFree).length,
    financedDevices: active.length,
    closedAgreements: closed.length,
  };
}

export interface QuotaUsage {
  used: number;
  quota: number;
  /** `1 of 2 used`, matching the mockup tile. */
  label: string;
  /** True when more locations are free than the deal allows. */
  over: boolean;
}

/**
 * Free-location quota, counted against locations that are actually free TODAY —
 * not against the order they were created in. An operator who comped a fourth
 * store needs to see the quota exceeded, not a tidy "1 of 2".
 */
export function freeQuotaUsage(locations: LocationRow[], quota?: number | null): QuotaUsage {
  const allowed = Math.max(0, quota ?? 1);
  const used = locations.filter((l) => isBillingLocation(l) && !!l.isFree).length;
  return { used, quota: allowed, label: `${used} of ${allowed} used`, over: used > allowed };
}
